/**
 * CoinPackPicker.jsx — drops 4 coin top-up tiers into Profile / Shop.
 *
 * Now region-aware: shows the matching primary + backup gateway pills
 * (Stripe / PayPal / Paystack / Flutterwave) and FX-converts the GBP
 * price to whatever currency the chosen gateway uses.
 */
import React, { useEffect, useState } from 'react';
import { Coins, Sparkles, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';
import GatewayPicker from './GatewayPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const CURRENCY_SYMBOL = { GBP: '£', USD: '$', EUR: '€', NGN: '₦', KES: 'KSh' };

const CoinPackPicker = () => {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  // Gateway choice — driven by <GatewayPicker />.
  const [gateway, setGateway] = useState(null);

  useEffect(() => {
    if (PAYMENTS_DISABLED) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/payments/coin-packs`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setCatalog(data);
      } catch (e) {
        if (alive) setError(typeof e?.message === 'string' ? e.message : 'Failed to load packs');
      }
    })();
    return () => { alive = false; };
  }, []);

  const buy = async (packId) => {
    setBusyId(packId);
    setError('');
    try {
      const body = {
        pack_id: packId,
        origin_url: window.location.origin,
        provider: gateway?.provider,
        is_diaspora_card: !!gateway?.is_diaspora_card,
      };
      const res = await authFetch(`${BACKEND_URL}/api/payments/coin-packs/checkout`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) {
        throw new Error(typeof data?.detail === 'string' ? data.detail : 'Checkout failed');
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Checkout failed');
      setBusyId('');
    }
  };

  if (PAYMENTS_DISABLED) {
    return (
      <div
        className="rounded-2xl border border-amber-300/35 bg-amber-400/10 p-5 text-sm text-amber-100"
        role="status"
        data-testid="coinpacks-payments-disabled"
      >
        <p className="font-bold">Coin top-ups are unavailable in this preview.</p>
        <p className="mt-1 text-xs text-amber-100/80">{PAYMENTS_UNAVAILABLE_MESSAGE}</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="coinpacks-loading">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  const { packs } = catalog;
  const currency = (gateway?.currency || catalog.currency || 'GBP').toUpperCase();
  const sym = CURRENCY_SYMBOL[currency] || '';

  return (
    <div data-testid="coinpacks-root">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-400" /> Top up coins
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            100 coins = £1.00 · platform spread applied per currency.
          </p>
        </div>
      </div>

      {/* Region-aware gateway picker. We feed it the "standard" pack's
          GBP price as the reference for the quote/best-price badge — the
          actual amount per pack is still computed from the per-currency
          ladder below, so the badge is directional, not authoritative. */}
      <div className="mb-3">
        <GatewayPicker
          onChange={setGateway}
          baseGbp={packs.find((p) => p.id === 'standard')?.price_gbp || packs[0]?.price_gbp}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {packs.map((p) => {
          // Prefer the new per-currency prices map, fall back to legacy.
          const price = (p.prices && p.prices[currency]) ??
            (currency === 'NGN' ? p.price_ngn : p.price_gbp);
          const isBest = !!p.best_value;
          const busy = busyId === p.id;
          // Format: NGN/KES → no decimals (don't subdivide cleanly).
          const formatted = (currency === 'NGN' || currency === 'KES')
            ? Number(price).toLocaleString(undefined, { maximumFractionDigits: 0 })
            : Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => buy(p.id)}
              disabled={!!busyId}
              className={`relative rounded-2xl border p-4 text-left transition-all ${
                isBest
                  ? 'bg-gradient-to-br from-amber-500/25 to-amber-700/15 border-amber-400/60 hover:border-amber-300'
                  : 'bg-slate-800/60 border-slate-700 hover:border-amber-500/50'
              } ${busyId ? 'opacity-60' : ''} disabled:cursor-not-allowed`}
              data-testid={`coinpack-${p.id}`}
            >
              {isBest && (
                <span className="absolute -top-2 right-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-slate-900">
                  <Sparkles className="w-2.5 h-2.5" /> Best
                </span>
              )}
              {(p.bonus_coins || 0) > 0 && (
                <span
                  className="absolute -top-2 left-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-900 shadow-md"
                  data-testid={`coinpack-bonus-${p.id}`}
                >
                  +{p.bonus_coins} bonus
                </span>
              )}
              <div className="flex items-center gap-2 text-amber-300 mb-1">
                <Coins className="w-4 h-4" />
                <span className="text-2xl font-black tabular-nums">
                  {p.total_coins || p.coins}
                </span>
                {(p.bonus_coins || 0) > 0 && (
                  <span className="text-[10px] font-bold text-emerald-300 leading-none">
                    ({p.coins}+{p.bonus_coins})
                  </span>
                )}
              </div>
              <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                {p.label}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-white">
                  {sym}{formatted}
                </span>
                {busy && <Loader2 className="w-4 h-4 text-amber-300 animate-spin" />}
              </div>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="mt-3 text-sm text-rose-300" data-testid="coinpacks-error">{error}</p>
      )}
    </div>
  );
};

export default CoinPackPicker;
